import React from "react";
import { Box, Text, IconButton, Flex } from "@chakra-ui/react";
import { RepeatIcon } from "@chakra-ui/icons";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title } from "chart.js";

ChartJS.register(ArcElement, Tooltip, Legend, Title);

const CollateralCard = ({
  totalPositions,
  totalCollateral,
  utilizationRate,
  availableCollateral,
  handleReload,
  wbtcBalance,
  wethBalance,
  linkBalance,
}) => {
  const accountTotal = (
    Number(totalCollateral) +
    Number(availableCollateral) +
    Number(wbtcBalance) +
    Number(wethBalance) +
    Number(linkBalance)
  ).toFixed(2);
  const total = Number(accountTotal);
  const usedPercent = ((Number(totalCollateral) / total) * 100).toFixed(1);
  const collateralPercent = (
    (Number(availableCollateral) / total) *
    100
  ).toFixed(1);
  const btcPercent = ((Number(wbtcBalance) / total) * 100).toFixed(1);
  const ethPercent = ((Number(wethBalance) / total) * 100).toFixed(1);
  const linkPercent = ((Number(linkBalance) / total) * 100).toFixed(1);

  const assets = [
    {
      label: `Used: $${Number(totalCollateral).toLocaleString()}`,
      value: Number(totalCollateral),
      color: "#c9c9cb", // Light Green (green.300)
      hover: "#48BB78", // Slightly darker (green.400)
    },
    {
      label: `Stable: $${Number(availableCollateral).toLocaleString()}`,
      value: Number(availableCollateral),
      color: "#2d8838", // Dark Green (green.800)
      hover: "#22543D", // Even darker on hover (green.900)
    },

    {
      label: `BTC: $${Number(wbtcBalance).toLocaleString()}`,
      value: Number(wbtcBalance),
      color: "#f7931a",
      hover: "#ed8936",
    },
    {
      label: `ETH: $${Number(wethBalance).toLocaleString()}`,
      value: Number(wethBalance),
      color: "#627eea",
      hover: "#A9A9A9",
    },
    {
      label: `LINK: $${Number(linkBalance).toLocaleString()}`,
      value: Number(linkBalance),
      color: "#345dd3",
      hover: "#3182CE",
    },
  ];

  // Sort assets by value (ascending)
  // Create a sorted version ONLY for the chart display
  const chartAssets = [...assets].sort((a, b) => a.value - b.value);

  // Extract data for chart
  const data = {
    labels: chartAssets.map((a) => a.label),
    datasets: [
      {
        data: chartAssets.map((a) => a.value),
        backgroundColor: chartAssets.map((a) => a.color),
        hoverBackgroundColor: chartAssets.map((a) => a.hover),
        borderWidth: 1,
      },
    ],
  };
  const imagePlugin = {
    id: "arcIcons",
    afterDraw(chart) {
      const { ctx } = chart;
      const meta = chart.getDatasetMeta(0);

      meta.data.forEach((arc, index) => {
        const { x, y } = arc.tooltipPosition();
        const rawLabel = chart.data.labels[index] || "";
        const label = rawLabel.toLowerCase();

        let imageSrc;

        if (label.includes("btc")) imageSrc = tokenImages.btc;
        else if (label.includes("eth")) imageSrc = tokenImages.weth;
        else if (label.includes("link")) imageSrc = tokenImages.link;
        else if (label.includes("stable")) imageSrc = tokenImages.stable;
        else if (label.includes("used")) imageSrc = tokenImages.blind;

        if (imageSrc) {
          const image = new Image();
          image.src = imageSrc;
          image.onload = () => {
            ctx.drawImage(image, x - 8, y - 8, 16, 16);
          };
        }
      });
    },
  };

  const options = {
    plugins: {
      //legend: { ... },
      title: { display: false },
      //tooltip: { ... },
      arcIcons: true, // Enable the plugin
      legend: {
        display: true,
        position: "bottom",
        labels: {
          font: { size: 12 },
        },
        title: { display: false },
        tooltip: {
          callbacks: {
            label: function (tooltipItem) {
              const value = Number(tooltipItem.raw);
              const percent = ((value / total) * 100).toFixed(1);
              return `${
                tooltipItem.label
              } - $${value.toLocaleString()} (${percent}%)`;
            },
          },
        },
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    cutout: "60%",
  };

  const tokenImages = {
    eth: "./eth.png",
    usdc: "./usdc.png",
    usdc_e: "./usdc.png", // Assuming USDC.e uses the same image as USDC
    link: "./chainlink-icon.png",
    usdt: "./usdt.png",
    weth: "./eth.png", // Assuming WETH uses the same image as LINK
    btc: "./btc.png", // Placeholder image for BTC
    wbtc: "./btc.png",
    blind: "./blindfold-logo-transparent1.png",
    stable: "./dollarSign3.png",
  };

  return (
    <Box
      w="100%"
      maxWidth="500px"
      borderWidth="1px"
      bg="white"
      boxShadow="md"
      fontSize="sm"
    >
      <Flex p={1} justify="space-between" align="center" bg="gray.200">
        <Text ml={1} fontWeight="bold">
          Overview
        </Text>
        {handleReload && (
          <IconButton
            size="xs"
            colorScheme="blue"
            icon={<RepeatIcon fontSize="16px" />}
            onClick={handleReload}
            aria-label="Reload"
          />
        )}
      </Flex>

      <Flex justify="space-evenly" align="center">
        <Text fontSize="sm">
          Positions: <strong>({totalPositions})</strong>
        </Text>
        <Text fontSize="sm">
          Total Balance: <strong>${accountTotal}</strong>
        </Text>
      </Flex>

      <Box
        position="relative"
        h={["200px", "220px", "240px"]}
        w="100%"
        maxWidth="500px"
      >
        <Doughnut data={data} options={options} plugins={[imagePlugin]} />

        <Text
          position="absolute"
          top="37%"
          left="50%"
          transform="translate(-50%, -50%)"
          fontSize="xs"
          fontWeight="bold"
          textAlign="center"
          lineHeight="short"
        >
          {chartAssets.map((asset, index) => {
            const percent = ((asset.value / total) * 100).toFixed(1);
            const label = asset.label.split(":")[0]; // Extract just the asset name
            return (
              <React.Fragment key={index}>
                {percent}% {label}
                <br />
              </React.Fragment>
            );
          })}
        </Text>
      </Box>
    </Box>
  );
};

export default CollateralCard;
